import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useI18n } from 'contexts/i18n'
import { useTasks } from 'contexts/tasks'
import { useMe } from 'contexts/me'
import { getPluralizedType } from 'pages/people/utilities'
import { capitalize } from 'utilities/string'
import { safeFormat } from 'utilities/date-time'
import { unpackField } from 'utilities/person'
import {
    DotColumns, Entities,
    Clamp,
    Links, Chip, FavIcon, LinkName, LinkUrl,
    Label,
    ExpiryStatus
} from './s'
import ConcernsUnit from 'pages/tasks/components/concerns-unit'
import Link from 'components/link'
import Person from 'components/person'
import Ellipsify from 'components/ellipsify'
import Paragraph from 'components/typography/paragraph'
import { ExternalLink } from 'styled-icons/feather'

const ViewTaskDetails = ({ task, setTask }) => {
    const { dateLocale: locale } = useI18n()

    const [claiming, setClaiming] = useState(false)

    const { assignTask } = useTasks()
    const { me } = useMe()

    const {
        id,
        description,
        links = [],
        parent: group,
        dueAt,
        completedAt,

        assignedGroup,
        assignedTo
    } = task

    const claimTask = async () => {
        setClaiming(true)
        const { ok, response } = await assignTask(task.id, me.id)
        setClaiming(false)

        ok && setTask(response)
    }

    let memberOfAssignedGroup = false
    if(assignedGroup) {
        const groupIds = (unpackField(me[getPluralizedType(assignedGroup.type)]) ?? [])
            .map(({ id }) => id)

        memberOfAssignedGroup = groupIds.includes(assignedGroup.id)
    }

    return (
        <>
            {(assignedGroup || assignedTo) && (
                <DotColumns>
                    {!!assignedGroup && (
                        <>
                            <Entities
                                entities={[assignedGroup]}
                                preview={true}
                                showAggregateIcon={true}
                                className="compact"
                                salt={`${id}:${getPluralizedType(assignedGroup.type)}:group`} />
                            <span>·</span>
                        </>
                    )}
                    {!!assignedTo && (
                        <Person
                            who={assignedTo}
                            asLink={true}
                            size={24}
                            className="compact" />
                    )}
                    {(!assignedTo && memberOfAssignedGroup) && (
                        <Link
                            onClick={claimTask}
                            className={`constructive${claiming ? ' loading' : ''}`}>
                            <FormattedMessage
                                id="action_assign_to_me"
                                defaultMessage="Assign to me" />
                        </Link>
                    )}
                </DotColumns>
            )}
            {!!description && (
                <Clamp lines={4}>{description}</Clamp>
            )}
            {!!links?.length && (
                <Links>
                    {links.map(({ name, url }, index) => (
                        <Chip
                            key={`task:${id}:links:${name}:${index}`}
                            href={url}
                            target="_blank">
                            <FavIcon
                                size={24}
                                src={url} />
                            <LinkName className="compact">
                                <Ellipsify
                                    text={name}
                                    head={5} />
                            </LinkName>
                            <LinkUrl className="compact">
                                <Ellipsify
                                    text={url}
                                    head={9}
                                    tail={9} />
                            </LinkUrl>
                            <ExternalLink size={16} />
                        </Chip>
                    ))}
                </Links>
            )}
            {!!group && (
                <>
                    <Label>
                        <FormattedMessage
                            id="label_regarding"
                            defaultMessage="Regarding" />
                    </Label>
                    <Paragraph className="caption interpoint-divider">
                        <span>
                            <ConcernsUnit concerns={group.concerns} />
                        </span>
                        <span>
                            {group?.title}
                            {!group?.title && (
                                <FormattedMessage
                                    id={`noun_${group.type}`}
                                    defaultMessage={capitalize(group.type)} />
                            )}
                        </span>
                    </Paragraph>
                </>
            )}
            {!!dueAt && (
                <>
                    <Label>
                        <FormattedMessage
                            id="noun_due_date"
                            defaultMessage="Due date" />
                    </Label>
                    <Paragraph>
                        {safeFormat(dueAt, 'PPP', { locale })}
                    </Paragraph>
                    {!completedAt && <ExpiryStatus to={dueAt} />}
                </>
            )}
        </>
    )
}

export default ViewTaskDetails
